
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    Box,
    Typography,
    Autocomplete,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    Chip,
    Stack
} from '@mui/material';
import { getCursosDeAsistencias, postEventoManual } from '../../services/asistencias.service';

export default function ModalCrearEventoManual({ open, onClose, onConfirm }) {
    const [formData, setFormData] = useState({
        id: '',
        nombre_curso: '',
        fecha_desde: '',
        fecha_hasta: '',
        comentario_horario: '',
        nombre_docente: '',
        apellido_docente: '',
        dias_evento: []
    });

    const [cursosDisponibles, setCursosDisponibles] = useState([]);
    const [autoGenerateDays, setAutoGenerateDays] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (open) {
            fetchCursos();
            // Reset form on open
            setFormData({
                id: '',
                nombre_curso: '',
                fecha_desde: '',
                fecha_hasta: '',
                comentario_horario: '',
                nombre_docente: '',
                apellido_docente: '',
                dias_evento: []
            });
            setAutoGenerateDays(true);
            setError('');
            setSuccessMessage('');
        }
    }, [open]);

    const fetchCursos = async () => {
        try {
            const cursos = await getCursosDeAsistencias();
            setCursosDisponibles(cursos);
        } catch (err) {
            console.error('Error fetching cursos:', err);
            // Non-blocking error, user can still type manually
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (name, value) => {
        setFormData(prev => {
            const newData = { ...prev, [name]: value };

            // Si está activada la autogeneración y tenemos ambas fechas, regeneramos los días
            if (autoGenerateDays && newData.fecha_desde && newData.fecha_hasta) {
                const days = generateDays(newData.fecha_desde, newData.fecha_hasta);
                newData.dias_evento = days;
            }
            return newData;
        });
    };

    const generateDays = (start, end) => {
        const days = [];
        let dt = new Date(start + 'T12:00:00'); // Asegura zona horaria/hora correcta para evitar problemas de día
        const endDt = new Date(end + 'T12:00:00');

        while (dt <= endDt) {
            days.push(dt.toISOString().split('T')[0]);
            dt.setDate(dt.getDate() + 1);
        }
        return days;
    };

    const handleAutoGenerateChange = (event) => {
        const checked = event.target.checked;
        setAutoGenerateDays(checked);
        if (checked && formData.fecha_desde && formData.fecha_hasta) {
            const days = generateDays(formData.fecha_desde, formData.fecha_hasta);
            setFormData(prev => ({ ...prev, dias_evento: days }));
        }
    };

    const handleRemoveDay = (dayToRemove) => {
        setFormData(prev => ({
            ...prev,
            dias_evento: prev.dias_evento.filter(day => day !== dayToRemove)
        }));
        // Si el usuario modifica manualmente, desactivamos la autogeneración para no sobreescribir
        if (autoGenerateDays) {
            setAutoGenerateDays(false);
        }
    };

    const handleSubmit = async () => {
        // Validaciones básicas
        if (!formData.id || !formData.nombre_curso || !formData.fecha_desde || !formData.fecha_hasta) {
            setError('Por favor complete todos los campos obligatorios (*)');
            return;
        }

        if (!formData.dias_evento || formData.dias_evento.length === 0) {
            setError('Debe haber al menos un día de evento.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Preparar objeto para el backend
            // Formato de fechas: ya vienen como yyyy-mm-dd del input type="date"

            const payload = {
                id: parseInt(formData.id),
                nombre_curso: formData.nombre_curso,
                fecha_desde: formData.fecha_desde,
                fecha_hasta: formData.fecha_hasta,
                comentario_horario: formData.comentario_horario,
                nombre_apellido_docente: `${formData.nombre_docente}, ${formData.apellido_docente}`.trim().replace(/^, /, '').replace(/, $/, ''),
                dias_evento: formData.dias_evento
            };

            await postEventoManual(payload);
            setSuccessMessage('Evento creado exitosamente');

            setTimeout(() => {
                onConfirm(); // Refresh parent data
                onClose();
            }, 1500);

        } catch (err) {
            setError(err.message || 'Error al crear el evento');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>
                Crear Evento Manualmente
            </DialogTitle>
            <DialogContent dividers>
                {/* Alertas */}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Número de Evento *"
                            fullWidth
                            name="id"
                            type="number"
                            value={formData.id}
                            onChange={handleChange}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                        <Autocomplete
                            freeSolo
                            options={cursosDisponibles.map(c => c.nombre)}
                            value={formData.nombre_curso}
                            onChange={(event, newValue) => {
                                setFormData(prev => ({ ...prev, nombre_curso: newValue }));
                            }}
                            onInputChange={(event, newInputValue) => {
                                setFormData(prev => ({ ...prev, nombre_curso: newInputValue }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Nombre del Curso *"
                                    fullWidth
                                    required
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Fecha Desde *"
                            type="date"
                            fullWidth
                            name="fecha_desde"
                            value={formData.fecha_desde}
                            onChange={(e) => handleDateChange('fecha_desde', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Fecha Hasta *"
                            type="date"
                            fullWidth
                            name="fecha_hasta"
                            value={formData.fecha_hasta}
                            onChange={(e) => handleDateChange('fecha_hasta', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={autoGenerateDays}
                                        onChange={handleAutoGenerateChange}
                                        color="primary"
                                    />
                                }
                                label="Autogenerar días del evento según el rango de fechas"
                            />

                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 1 }}>
                                Días de cursado (puedes eliminar los que no correspondan):
                            </Typography>

                            <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                maxHeight: '150px',
                                overflowY: 'auto',
                                p: 1,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}>
                                {formData.dias_evento.map((day) => (
                                    <Chip
                                        key={day}
                                        label={day}
                                        onDelete={() => handleRemoveDay(day)}
                                        size="small"
                                    />
                                ))}
                                {formData.dias_evento.length === 0 && (
                                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                        No hay días seleccionados.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Comentario de Horario"
                            fullWidth
                            name="comentario_horario"
                            value={formData.comentario_horario}
                            onChange={handleChange}
                            placeholder="Ej: 10:00 - 12:00"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Nombre del Docente"
                            fullWidth
                            name="nombre_docente"
                            value={formData.nombre_docente}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Apellido del Docente"
                            fullWidth
                            name="apellido_docente"
                            value={formData.apellido_docente}
                            onChange={handleChange}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
                <Button onClick={onClose} color="inherit" disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {loading ? 'Guardando...' : 'Guardar Evento'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
